import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useTheme } from '@emotion/react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import PropTypes from 'prop-types';
import { useNavigate, generatePath } from 'react-router-dom';
import styled from '@emotion/styled';

// Our Components
import DataTableToolBar from 'components/DataTable/DataTableToolBar';
import DataTableSortingArrow from 'components/DataTable/DataTableSortingArrow';
import OnboardingTasksNoRowOverlay from 'components/DataTable/OnboardingTasksNoRowOverlay';
import NoRowsOverlay from 'components/DataTable/NoRowsOverlay';
import NoLoanTasksOverlay from 'components/DataTable/NoLoanTasksOverlay';

// Our routes
import { ADVISOR_WALLET_ROUTE } from 'routes';

// Our utils
import { dataIsValid } from 'shared/utils';

const CustomArrowDropDownIcon = styled(ArrowDropDownIcon)`
	color: #000000 !important;
`;

const CustomArrowDropUpIcon = styled(ArrowDropUpIcon)`
	color: #000000 !important;
`;

function DataTable({
	columns,
	disableSelectionOnClick,
	disableCursor,
	enableToolbar,
	handleRowClick,
	getRowClassName,
	noLoanTasks,
	pageSize,
	rowHeight,
	rows,
	rowsPerPageOptions,
	sx,
	sortingModel,
	withOnboardNowOverlay,
	withRowColorCheck,
	rowColorCheckValidator
}) {
	const SoraTheme = useTheme();
	const navigate = useNavigate();

	const setClientIdAndNavigate = (clientId) => {
		const soraClientUrl = generatePath(
			`${ADVISOR_WALLET_ROUTE}${clientId}`,
			{
				clientId
			}
		);
		navigate(soraClientUrl);
	};

	const setNoLoanState = () => {
		if (withOnboardNowOverlay && !noLoanTasks) {
			return OnboardingTasksNoRowOverlay;
		}
		if (noLoanTasks) {
			return NoLoanTasksOverlay;
		}
		return NoRowsOverlay;
	};

	const hasRows = rows.length > 0;

	const BoxHeight = hasRows ? 'auto' : 300;

	return (
		<Box sx={{ width: '100%', height: BoxHeight }}>
			<DataGrid
				autoPageSize
				autoHeight={hasRows}
				rows={rows}
				disableSelectionOnClick={disableSelectionOnClick ?? false}
				columns={columns}
				pageSize={pageSize} // example is 5
				rowsPerPageOptions={[rowsPerPageOptions]}
				rowHeight={rowHeight}
				headerHeight={48}
				initialState={{
					sorting: {
						sortModel: sortingModel || []
					}
				}}
				onRowClick={(rowData) => {
					const { row } = rowData;
					if (handleRowClick) {
						handleRowClick(row);
					} else if ('status' in row) {
						if (row.status === 'ACTIVE') {
							const { clientId } = row;
							setClientIdAndNavigate(clientId);
						}
					} else {
						const { clientId } = row;
						setClientIdAndNavigate(clientId);
					}
				}}
				getRowClassName={(params) => {
					if (getRowClassName) {
						return getRowClassName(params);
					}
					return 'MuiDataGrid-row';
				}}
				sx={{
					...sx,
					backgroundColor:
						SoraTheme.palette.gradient
							.soraBlueToWhiteLinearGradient,
					'& .MuiDataGrid-columnSeparator': {
						display: 'none'
					},
					'& .MuiDataGrid-row': {
						cursor: disableCursor ? 'default' : 'pointer'
					},
					'& .MuiDataGrid-row-no-cursor': {
						cursor: 'default'
					},
					'& .MuiDataGrid-row-special': {
						backgroundColor: `${SoraTheme.palette.primary.lightShade}`,
						'&:hover': {
							backgroundColor: `${SoraTheme.palette.primary.mainHover}`
						},
						'&.Mui-selected': {
							backgroundColor: `${SoraTheme.palette.primary.main}`,
							'&:hover': {
								backgroundColor: `${SoraTheme.palette.primary.mainHover}`
							}
						}
					},
					'& .MuiDataGrid-columnHeader': {
						paddingLeft: 0
					},
					'& .MuiDataGrid-cell': {
						paddingLeft: 0
					},
					'& .MuiDataGrid-columnHeaderTitleContainer': {
						paddingTop: '8px',
						paddingBottom: '8px'
					},
					'& .MuiDataGrid-columnHeader:focus': {
						outline: 'none'
					},
					'& .MuiDataGrid-columnHeader:focus-within': {
						outline: 'none'
					},
					'& .MuiDataGrid-cell:focus': {
						outline: 'none'
					},
					'.MuiDataGrid-iconButtonContainer': {
						visibility: 'visible'
					},
					'.MuiDataGrid-sortIcon': {
						opacity: 'inherit'
					},
					// Target the scrollbar track
					'& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
						backgroundColor: '#f1f1f1' // Or any desired background color
					},
					// Target the scrollbar thumb
					'& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
						backgroundColor: '#888', // Or any desired thumb color
						borderRadius: '4px' // Optional: add rounded corners
					},
					// Make the scrollbar always visible
					'& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
						width: '8px', // Set desired width for vertical scrollbar
						height: '8px', // Set desired height for horizontal scrollbar
						display: 'block' // Crucial for always showing
					},
					border: 'none'
				}}
				components={{
					ColumnSortedAscendingIcon: CustomArrowDropDownIcon,
					ColumnSortedDescendingIcon: CustomArrowDropUpIcon,
					ColumnUnsortedIcon: DataTableSortingArrow,
					Toolbar: enableToolbar ? DataTableToolBar : null,
					NoRowsOverlay: setNoLoanState()
				}}
			/>
		</Box>
	);
}

const columnItemShape = {
	field: PropTypes.string.isRequired,
	headerName: PropTypes.string.isRequired,
	flex: PropTypes.number,
	props: PropTypes.object // props specifies mx props check component ColumnItem for links.
};

// Rows shape will be dependant on the columnItemShape.
DataTable.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.shape(columnItemShape)).isRequired,
	disableSelectionOnClick: PropTypes.bool,
	enableToolbar: PropTypes.bool,
	pageSize: PropTypes.number,
	rowsPerPageOptions: PropTypes.number,
	rowHeight: PropTypes.number,
	withOnboardNowOverlay: PropTypes.bool,
	noLoanTasks: PropTypes.bool
};

DataTable.defaultProps = {
	disableSelectionOnClick: false,
	enableToolbar: false,
	pageSize: 10,
	rowsPerPageOptions: 10,
	rowHeight: 52, // Default by MUI I.e. What we had
	withOnboardNowOverlay: false,
	noLoanTasks: false
};

export default DataTable;
